import * as A from 'fp-ts/lib/Array';
import {flow} from 'fp-ts/lib/function';
import * as NEA from 'fp-ts/lib/NonEmptyArray';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';

export const joinStringArray = (delimiter: string) => (stringToJoin: string[]) => stringToJoin.join(delimiter);

export const toSentenceCase = flow(
  (stringToCase: string) => stringToCase.split(''),
  NEA.fromArray,
  O.map(charactersToCase => {
    const capitalizedFirstCharacter = NEA.head(charactersToCase).toUpperCase();
    const lowerCasedTail = pipe(
      NEA.tail(charactersToCase),
      A.map(character => character.toLowerCase()),
      joinStringArray('')
    );

    return capitalizedFirstCharacter + lowerCasedTail;
  }),
  O.getOrElse(() => '')
);

export const toKebabCase = flow(
  (stringToCase: string) => stringToCase.split(' '),
  NEA.fromArray,
  O.map(charactersToCase => {
    const lowerCasedTail = pipe(
      charactersToCase.map(character => character.toLowerCase()),
      joinStringArray('-')
    );

    return lowerCasedTail;
  }),
  O.getOrElse(() => '')
);

export const fromKebabCase = flow(
  (stringToCase: string) => stringToCase.split('-'),
  NEA.fromArray,
  O.map(charactersToCase => {
    const lowerCasedTail = pipe(
      charactersToCase.map(character => character.toLowerCase()),
      joinStringArray(' ')
    );

    return lowerCasedTail;
  }),
  O.getOrElse(() => '')
);

export const toProperCase = (wordToCase: string) => {
  return wordToCase.substring(0, 1).toUpperCase() + wordToCase.substring(1).toLowerCase();
};

export const toTitleCase = flow(
  (stringToCase: string) => stringToCase.split(' '),
  NEA.fromArray,
  O.map(wordsToCase => {
    const firstWord = NEA.head(wordsToCase);

    const capitalizedFirstWord = toProperCase(firstWord) + ' ';

    const capitalizedTail = pipe(
      NEA.tail(wordsToCase),
      A.map(word => toProperCase(word)),
      joinStringArray(' ')
    );

    return capitalizedFirstWord + capitalizedTail;
  }),
  O.getOrElse(() => '')
);

export const toPascalCase = flow(
  (stringToCase: string) => stringToCase.split(' '),
  NEA.fromArray,
  O.map(wordsToCase => {
    const firstWord = NEA.head(wordsToCase);

    const capitalizedFirstWord = toProperCase(firstWord);

    const capitalizedTail = pipe(
      NEA.tail(wordsToCase),
      A.map(word => toProperCase(word)),
      joinStringArray('')
    );

    return capitalizedFirstWord + capitalizedTail;
  }),
  O.getOrElse(() => '')
);

export const capitalizeSubString = (str: string | null | undefined, startIndex: number, endIndex: number) =>
  pipe(
    O.fromNullable(str),
    O.fold(
      () => '',
      s => {
        const validStartIndex = Math.max(0, startIndex);
        const validEndIndex = Math.min(endIndex, s.length);

        return (
          s.slice(0, validStartIndex) + s.slice(validStartIndex, validEndIndex).toUpperCase() + s.slice(validEndIndex)
        );
      }
    )
  );